// src/pages/LoginScreen.tsx — Phase 1 Slice B commit 2.
//
// Two paths to the home screen:
//   1. Primary — SSO. Brass-tinted Microsoft button kicks to /.auth/login/aad;
//      SWA handles the OAuth dance and lands the user on / with their cookie.
//   2. Secondary — PIN. Collapsible section reveals email field + PinDots +
//      NumPad. POST /api/auth/login { email, pin } per auth.md §5.2. On 200
//      we drop the auth hint plus the email into localStorage so a returning
//      user sees the PIN section pre-opened with their email pre-filled.
//
// A third, additive affordance sits BELOW the standard card: "Help me sign in",
// which opens the LoginHelpPanel (an in-place assistant for already-approved
// volunteers who are stuck). It does not alter the standard sign-in above.
//
// Adheres to the audit-resolved decisions in STATUS §3:
//   - PIN regex /^[KFWT]\d[KFWT]\d{3}$/ (6 chars, letters in positions 0/2)
//   - 429 surfaces a generic "Too many attempts" until the rate-limit body
//     shape is finalised (Phase 1 closer).
//   - 401 shakes the dots and clears the PIN buffer, BUT keeps the email so
//     the user retries without re-typing.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { writeAuthHint } from "../auth";
import { C, F } from "../theme";
import {
  AuthShell,
  Button,
  Eyebrow,
  Field,
  Icons,
  NumPad,
  PIN_LENGTH,
  PIN_REGEX,
  PinDots,
} from "../components/ui";
import { LoginHelpPanel } from "./LoginHelpPanel";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AUTH_HINT_KEY = "nymr_auth_hint";
const PIN_EMAIL_KEY = "nymr_pin_email";

function loadStored(key: string): string {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(key) ?? "";
}

export function LoginScreen() {
  const navigate = useNavigate();

  const [pin, setPin] = useState("");
  const [email, setEmail] = useState(() => loadStored(PIN_EMAIL_KEY));
  const [pinOpen, setPinOpen] = useState(() => loadStored(AUTH_HINT_KEY) === "pin");
  const [shake, setShake] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Auto-clear the inline error after 4s — matches the prototype's transient
  // signal-tinted card behaviour. Lockout messages persist (don't auto-clear)
  // because the lock itself persists across the 4s window.
  useEffect(() => {
    if (!error || lockedUntil) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error, lockedUntil]);

  // Reset the shake animation flag after the keyframes complete.
  useEffect(() => {
    if (!shake) return;
    const t = setTimeout(() => setShake(false), 500);
    return () => clearTimeout(t);
  }, [shake]);

  function pressChar(c: string) {
    if (submitting) return;
    if (pin.length >= PIN_LENGTH) return;
    setPin(pin + c);
  }

  function pressDelete() {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  }

  async function submit() {
    if (submitting) return;

    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!PIN_REGEX.test(pin)) {
      setError("PIN format is letter, number, letter, three numbers (e.g. K1F234).");
      setShake(true);
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch<{ ok: true }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: trimmed, pin }),
      });
      // Success — record the hint + email so the next visit pre-fills, then
      // hand off to the authed tree (RequireAuth will let us through).
      writeAuthHint("pin");
      localStorage.setItem(PIN_EMAIL_KEY, trimmed);
      navigate("/", { replace: true });
    } catch (e: unknown) {
      const status = e instanceof ApiError ? e.status : 0;
      const body = e instanceof ApiError ? (e.body as Record<string, unknown> | null) : null;
      if (status === 423) {
        // Account locked — show lockedUntil and disable the PIN entry.
        const until = typeof body?.lockedUntil === "string" ? body.lockedUntil : null;
        setLockedUntil(until);
        setError(until ? `Account locked until ${formatLockedUntil(until)}.` : "Account locked.");
        setPin("");
      } else if (status === 401) {
        const attemptsLeft = typeof body?.attemptsLeft === "number" ? body.attemptsLeft : null;
        if (attemptsLeft === null) {
          setError("Invalid email or PIN. Try again.");
        } else if (attemptsLeft <= 0) {
          // Defensive: server should have returned 423, but keep the UI honest.
          setError("Account locked.");
        } else {
          setError(`Wrong PIN. ${attemptsLeft} ${attemptsLeft === 1 ? "attempt" : "attempts"} left.`);
        }
        setShake(true);
        setPin("");
      } else if (status === 429) {
        // Backend will ship Retry-After when the rate-limit shape is finalised
        // in Phase 1 closer; until then a generic message.
        setError("Too many attempts. Try again in a few minutes.");
        setPin("");
      } else {
        const msg = e instanceof ApiError ? e.message : "Couldn't sign in. Try again.";
        setError(msg || "Couldn't sign in. Try again.");
      }
      setSubmitting(false);
    }
  }

  function formatLockedUntil(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  }

  return (
    <AuthShell>
      {helpOpen ? (
        <LoginHelpPanel onExit={() => setHelpOpen(false)} />
      ) : (
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          padding: "32px 4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Brand mark */}
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 14,
            background: C.brassDk,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 14,
            boxShadow: "inset 0 1px 0 rgba(244,226,168,0.3), 0 6px 16px rgba(122,84,11,0.35)",
          }}
        >
          <Icons.Train s={30} c={C.brassGlow} />
        </div>

        <Eyebrow style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>
          North Yorkshire Moors Railway
        </Eyebrow>
        <h1
          style={{
            fontFamily: F.serif,
            fontSize: 28,
            fontWeight: 700,
            color: C.ink,
            margin: "4px 0 6px",
            letterSpacing: -0.6,
            textAlign: "center",
          }}
        >
          NYMR Info Hub
        </h1>
        <p
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 14,
            color: C.inkSoft,
            marginTop: 0,
            marginBottom: 24,
            fontWeight: 600,
            textAlign: "center",
            maxWidth: 320,
          }}
        >
          Sign in to see today's duty, contacts and notices.
        </p>

        {/* Primary: SSO */}
        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            writeAuthHint("sso");
            window.location.href = "/.auth/login/aad";
          }}
          style={{ maxWidth: 320, minHeight: 56, fontSize: 16, marginBottom: 12 }}
        >
          <MicrosoftSquares /> Sign in with NYMR account
        </Button>
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 12.5,
            color: C.inkSoft,
            fontWeight: 600,
            marginBottom: 18,
            textAlign: "center",
            maxWidth: 280,
            lineHeight: 1.4,
          }}
        >
          For staff with an @nymr.co.uk Microsoft account
        </div>

        <OrDivider />

        {/* Secondary: PIN */}
        {!pinOpen ? (
          <Button
            variant="secondary"
            fullWidth
            onClick={() => setPinOpen(true)}
            style={{ maxWidth: 320, minHeight: 52, fontSize: 15 }}
          >
            <Icons.Pin s={18} c={C.brassDk} /> I have a 6-character PIN
          </Button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%", maxWidth: 360 }}>
            <Eyebrow style={{ textAlign: "center", marginBottom: 10 }}>Sign in with PIN</Eyebrow>
            <Field
              label="Your personal email address"
              value={email}
              onChange={setEmail}
              type="email"
              ph="you@example.com"
              disabled={submitting || lockedUntil !== null}
            />
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 12,
                color: C.inkSoft,
                marginBottom: 14,
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Letter · number · letter · 3 numbers
            </div>
            <PinDots value={pin} shake={shake} />
            {error && (
              <div
                role="alert"
                style={{
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: C.signalBg,
                  border: "1px solid rgba(184,58,43,0.22)",
                  color: C.signalDk,
                  fontFamily: F.body,
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                {error}
              </div>
            )}
            <NumPad
              length={pin.length}
              onPress={pressChar}
              onDelete={pressDelete}
              onSubmit={submit}
              disabled={submitting || lockedUntil !== null}
            />
          </div>
        )}

        {/* Footer links */}
        <div style={{ marginTop: 24, display: "flex", gap: 18, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => navigate("/register")}
            style={{
              background: "none",
              border: "none",
              color: C.brassDk,
              fontFamily: F.body,
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              padding: "4px 0",
            }}
          >
            New here? Register
          </button>
          <div style={{ width: 1, height: 12, background: C.hairline }} />
          <button
            type="button"
            onClick={() => navigate("/forgot-pin")}
            style={{
              background: "none",
              border: "none",
              color: C.inkSoft,
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              padding: "4px 0",
            }}
          >
            Forgotten PIN?
          </button>
        </div>

        {/* Help me sign in — additive assistant for already-approved volunteers
            who are stuck. Does not change the standard sign-in above. */}
        <div style={{ marginTop: 18, width: "100%", maxWidth: 320 }}>
          <Button
            variant="soft"
            fullWidth
            onClick={() => setHelpOpen(true)}
            style={{ minHeight: 46, fontSize: 14 }}
          >
            Help me sign in
          </Button>
        </div>
      </div>
      )}
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Inline subcomponents
// ---------------------------------------------------------------------------

function MicrosoftSquares() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" style={{ flexShrink: 0 }} aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

function OrDivider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", maxWidth: 320, marginBottom: 18 }}>
      <div style={{ flex: 1, height: 1, background: C.hairline }} />
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12, color: C.inkMute, fontWeight: 600 }}>or</div>
      <div style={{ flex: 1, height: 1, background: C.hairline }} />
    </div>
  );
}
